/**
 * Festivals — admin dashboard.
 *
 * Replaces the previous "list of festival_event_takeover ad_campaigns"
 * table with a festival-first dashboard. Each festival is a card with:
 *   - Hero image + theme color + countdown
 *   - Revenue thermometer (booked / total potential)
 *   - Sponsor stack (one row per filled tier slot, "OPEN" rows for the
 *     tiers we haven't sold yet)
 *   - Quick actions: launch wizard pre-selected to a specific tier,
 *     open the pitch-deck print view, or jump to legacy edit dialogs
 *     for a sponsoring campaign.
 *
 * Why this shape
 * ──────────────
 * Festivals are stacked-monetization products — one festival can carry
 * five or six separate sponsor cheques across different tiers. The old
 * list view conflated festival ↔ campaign, so adding a second sponsor
 * was indistinguishable from creating a duplicate of the first. The
 * dashboard puts the *festival* at the centre and exposes every
 * sponsor slot (filled or empty) as a sales actionable.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'wouter';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, CalendarDays, MapPin, Sparkles, ExternalLink,
  Music, PartyPopper, Crown, Mic, Route, Pin, Moon,
  ChevronRight, Printer, Trophy, ShoppingBasket, Flag,
  Pencil, Ticket, Pause, Play, Trash2, Eye, RotateCcw,
  Loader2, Settings, EyeOff, PowerOff, Power, Archive,
  Info, Smartphone, Car, AlertCircle,
  Video, Image as ImageIcon, Globe,
} from 'lucide-react';
import CampaignWizard from './wizard/CampaignWizard';
import FestivalEditorDialog from './FestivalEditorDialog';
import type {
  FestivalEntry, FestivalTierKey,
} from '@/stores/campaignWizardStore';
import { getWizardStore } from '@/stores/campaignWizardStore';

// ── Tier visual identity ──────────────────────────────────────────────────

const TIER_VISUAL: Record<
  FestivalTierKey,
  { label: string; icon: any; accent: string }
> = {
  title:         { label: 'Title sponsor',       icon: Crown, accent: '#a855f7' },
  stage:         { label: 'Stage sponsor',       icon: Mic,   accent: '#ec4899' },
  corridor:      { label: 'Corridor sponsor',    icon: Route, accent: '#0ea5e9' },
  venue_partner: { label: 'Venue partner',       icon: Pin,   accent: '#10b981' },
  after_party:   { label: 'After-party sponsor', icon: Moon,  accent: '#f59e0b' },
  sub_event:     { label: 'Sub-event sponsor',   icon: Mic,   accent: '#ec4899' },
};

// ── Category visual identity ──────────────────────────────────────────────
// Shared with the editor dialog; kept here for the filter tabs and
// per-card category badge.

type Category =
  | 'all'
  | 'festival' | 'concert' | 'party' | 'sports' | 'market' | 'civic' | 'private_event';

const CATEGORY_TABS: { key: Category; label: string; icon: any; tone: string }[] = [
  { key: 'all',           label: 'All events', icon: Sparkles,       tone: 'text-gray-300' },
  { key: 'festival',      label: 'Festivals',  icon: Music,          tone: 'text-purple-300' },
  { key: 'concert',       label: 'Concerts',   icon: Mic,            tone: 'text-pink-300' },
  { key: 'party',         label: 'Parties',    icon: PartyPopper,    tone: 'text-amber-300' },
  { key: 'sports',        label: 'Sports',     icon: Trophy,         tone: 'text-green-300' },
  { key: 'market',        label: 'Markets',    icon: ShoppingBasket, tone: 'text-cyan-300' },
  { key: 'civic',         label: 'Civic',      icon: Flag,           tone: 'text-emerald-300' },
  { key: 'private_event', label: 'Private',    icon: Sparkles,       tone: 'text-violet-300' },
];

const CATEGORY_LABEL: Record<string, string> = {
  festival: 'Festival',
  concert: 'Concert',
  party: 'Party',
  sports: 'Sports',
  market: 'Market',
  civic: 'Civic',
  private_event: 'Private',
};

const TIER_ORDER: FestivalTierKey[] = [
  'title',
  'stage',
  'corridor',
  'venue_partner',
  'after_party',
];

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString('en-US', fmt)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', { ...fmt, year: 'numeric' })}`;
}

function fmtMoney(n: number): string {
  return `EC$${Math.round(n).toLocaleString()}`;
}

function fmtCountdown(
  days: number,
  isLive: boolean,
): { label: string; tone: 'live' | 'imminent' | 'upcoming' | 'ended' } {
  // PRIMARY signal: is_live comes from the database as
  // `NOW() BETWEEN starts_at AND ends_at` and is the only authoritative
  // way to know whether the event is currently in its window. Without
  // this we used to infer "live" from `days_until_start < 0`, which is
  // wrong because days_until_start stays negative AFTER the event ends
  // — a 2-day event that finished a week ago would still be flagged
  // "Live now" for the 30 days the RPC keeps it visible.
  if (isLive) return { label: 'Live now', tone: 'live' };

  // Past events: anything with a start date in the past that isn't
  // currently live has, by definition, ended. (Edge case: an event
  // that hasn't started yet but is "today" — handled below.)
  if (days < 0) return { label: 'Ended', tone: 'ended' };

  if (days === 0) return { label: 'Today', tone: 'imminent' };
  if (days <= 7)  return { label: `In ${days} day${days === 1 ? '' : 's'}`, tone: 'imminent' };
  if (days <= 60) return { label: `In ${days} days`, tone: 'upcoming' };
  const months = Math.round(days / 30);
  return { label: `In ${months} month${months === 1 ? '' : 's'}`, tone: 'upcoming' };
}

// ── Component ─────────────────────────────────────────────────────────────

// Status filter — mirrors the `p_status` parameter on the admin_list_festivals
// RPC. 'active' is the default (= what operators see on the in-app maps);
// 'inactive' lets them find events they paused so they can reactivate them;
// 'all' is the catch-all for archive review.
type StatusFilter = 'active' | 'inactive' | 'all';

const STATUS_TABS: {
  key: StatusFilter;
  label: string;
  icon: any;
  tone: string;
  hint: string;
}[] = [
  {
    key: 'active',
    label: 'Active',
    icon: Power,
    tone: 'text-emerald-300',
    hint: 'Visible on app maps',
  },
  {
    key: 'inactive',
    label: 'Inactive',
    icon: PowerOff,
    tone: 'text-amber-300',
    hint: 'Hidden from app maps (paused or archived)',
  },
  {
    key: 'all',
    label: 'All',
    icon: Archive,
    tone: 'text-gray-300',
    hint: 'Active + inactive combined',
  },
];

export default function Festivals() {
  const { seesMoney } = useAdminAuth();
  const [festivals, setFestivals] = useState<FestivalEntry[]>([]);
  const [statusCounts, setStatusCounts] = useState<{
    active: number; inactive: number; total: number;
  }>({ active: 0, inactive: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard launching state — when the user clicks an "OPEN" tier slot
  // we open the wizard pre-selected to that festival + tier so they
  // jump directly to the brand picker.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPreselect, setWizardPreselect] = useState<{
    festival: FestivalEntry; tier: FestivalTierKey; subEventSlug: string | null;
  } | null>(null);

  // Editor dialog state — for creating new events or editing existing
  // ones in place. Null `editing` means create mode; populated means edit.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<FestivalEntry | null>(null);

  // Category filter — drives the tab strip below the header.
  const [category, setCategory] = useState<Category>('all');

  // Status filter — drives the second tab strip (Active / Inactive / All).
  // The bug we're fixing: previously the admin list hard-filtered to
  // is_active = TRUE only, so once an operator toggled inactive there
  // was no UI to find the event again. Defaults to 'active' so the
  // dashboard's first-load behaviour is unchanged.
  const [status, setStatus] = useState<StatusFilter>('active');

  // Inactive count loaded alongside the active list so we can show a
  // small badge / nudge "you have 3 archived events" even while on the
  // Active tab. Without this the operator wouldn't know there was
  // anything to look at on the Inactive tab.
  const [inactiveCount, setInactiveCount] = useState(0);

  const fetchFestivals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_festivals', {
        p_horizon_days: 365,
        // Include past events when viewing inactive/all so an archived
        // event from last weekend is still reachable.
        p_include_past: status !== 'active',
        p_status: status,
      });
      if (rpcErr) throw rpcErr;
      const env = data as {
        ok?: boolean;
        festivals?: FestivalEntry[];
        counts?: { active: number; inactive: number; total: number };
        error?: string;
      };
      if (env?.ok === false) throw new Error(env.error || 'forbidden');
      setFestivals(env?.festivals ?? []);
      if (env?.counts) {
        setStatusCounts(env.counts);
        setInactiveCount(env.counts.inactive ?? 0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load festivals');
      toast.error(err.message || 'Failed to load festivals');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchFestivals(); }, [fetchFestivals]);

  // Quick activate/deactivate toggle — used by the per-card power button
  // and surfaces a toast confirming the operator's action. Re-fetches
  // afterwards so the row hops to whichever tab it now belongs in.
  const handleToggleActive = useCallback(
    async (festival: FestivalEntry) => {
      const next = !festival.is_active;
      try {
        const { data, error: rpcErr } = await supabase.rpc(
          'admin_set_festival_active',
          { p_id: festival.id, p_active: next },
        );
        if (rpcErr) throw rpcErr;
        const env = data as { ok?: boolean; error?: string };
        if (env?.ok === false) throw new Error(env.error || 'failed');
        toast.success(
          next
            ? `${festival.name} is now active and visible on app maps.`
            : `${festival.name} is now inactive and hidden from app maps.`,
        );
        await fetchFestivals();
      } catch (err: any) {
        toast.error(err.message || 'Failed to update event status');
      }
    },
    [fetchFestivals],
  );

  // Apply the active category filter. Done client-side because the
  // dataset is small (12 events seeded; expected to stay under
  // ~200 even with steady ops growth) and avoids a round-trip when
  // toggling tabs.
  const visibleFestivals = useMemo(() => {
    if (category === 'all') return festivals;
    return festivals.filter((f) => (f as any).category === category);
  }, [festivals, category]);

  // Per-category counts so the tabs can show how many events are in
  // each bucket.
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: festivals.length };
    festivals.forEach((f) => {
      const c = (f as any).category || 'festival';
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [festivals]);

  // Total revenue summary across all upcoming festivals (drives the
  // top-of-page hero card). NOTE: totals are scoped to the *visible*
  // category so the operator sees per-category revenue when they
  // tab around — useful for "how much are we making from parties
  // alone?" sorts of questions.
  const totals = useMemo(() => {
    let booked = 0;
    let pending = 0;
    let totalSlots = 0;
    let filledSlots = 0;
    let liveCount = 0;
    visibleFestivals.forEach((f) => {
      if (f.revenue) {
        booked += f.revenue.booked_xcd ?? 0;
        pending += f.revenue.pending_xcd ?? 0;
        totalSlots += f.revenue.slot_count_total;
        filledSlots += f.revenue.slot_count_filled;
      }
      if (f.is_live) liveCount += 1;
    });
    return { booked, pending, totalSlots, filledSlots, liveCount };
  }, [visibleFestivals]);

  // Launch wizard pre-selected. Pre-fills the festival mode store
  // BEFORE opening, so the FestivalPicker shows up "primed" (the
  // operator can still change their mind by clicking the back arrow).
  const launchWizardForSlot = (
    festival: FestivalEntry,
    tier: FestivalTierKey,
    subEventSlug: string | null = null,
  ) => {
    setWizardPreselect({ festival, tier, subEventSlug });
    setWizardOpen(true);
  };

  // After the wizard mounts, if we have a preselect, push it into
  // the store. Done as an effect because the store reset happens
  // inside the wizard's open effect.
  useEffect(() => {
    if (wizardOpen && wizardPreselect) {
      // Defer one tick so the wizard's reset effect runs first.
      const t = setTimeout(() => {
        const store = getWizardStore('festival').getState();
        store.selectFestivalAndTier(
          wizardPreselect.festival,
          wizardPreselect.tier,
          wizardPreselect.subEventSlug ?? null,
        );
      }, 50);
      return () => clearTimeout(t);
    }
  }, [wizardOpen, wizardPreselect]);

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 bg-gray-800/50 rounded-xl" />
        <Skeleton className="h-48 bg-gray-800/50 rounded-xl" />
        <Skeleton className="h-48 bg-gray-800/50 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-rose-500/10 border-rose-500/30 p-6">
        <p className="text-rose-200">Failed to load festivals: {error}</p>
        <p className="text-rose-200/70 text-xs mt-2">
          If this is the first deploy, ensure migrations{' '}
          <code className="font-mono text-cyan-300">20260620640000_festivals_first_class</code>
          {' '}and{' '}
          <code className="font-mono text-cyan-300">20260620650000_seed_dominica_festivals</code>
          {' '}have been applied.
        </p>
        <Button onClick={fetchFestivals} className="mt-3">Retry</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Music className="w-6 h-6 text-purple-400" /> Festivals & events
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Stacked monetization for festivals, concerts, parties, sports,
            markets, and civic events. Every event has multiple sponsorship
            slots you can sell separately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="border-gray-700 text-gray-200 hover:bg-white/5"
          >
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
          <Button
            onClick={() => {
              setWizardPreselect(null);
              setWizardOpen(true);
            }}
            className="bg-purple-600 hover:bg-purple-500 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Sponsor a slot
          </Button>
        </div>
      </div>

      {/* ── Status filter tabs ───────────────────────────────────────── */}
      {/* Lives ABOVE the category strip because "is this event visible on
          the app maps?" is the higher-priority question — it determines
          whether the rows below even appear. Without this strip, paused
          events have nowhere to surface and can't be re-activated. */}
      <div className="flex items-center gap-1 flex-wrap bg-gray-900/40 border border-gray-800 rounded-xl p-1">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = status === tab.key;
          const count =
            tab.key === 'active'
              ? statusCounts.active
              : tab.key === 'inactive'
                ? statusCounts.inactive
                : statusCounts.total;
          return (
            <Tooltip key={tab.key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setStatus(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    active
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${active ? tab.tone : ''}`} />
                  {tab.label}
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                    active ? 'bg-white/15 text-white' : 'bg-gray-800 text-gray-500'
                  }`}>
                    {count}
                  </span>
                  {/* Pull attention to the Inactive tab when something
                      is archived but the operator isn't looking at it.
                      Solves the "I marked it inactive but now I can't
                      find it" bug head-on. */}
                  {tab.key === 'inactive' && inactiveCount > 0 && !active && (
                    <span
                      className="ml-0.5 w-1.5 h-1.5 rounded-full bg-amber-300"
                      aria-label={`${inactiveCount} inactive events`}
                    />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
                {tab.hint}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* ── Category filter tabs ─────────────────────────────────────── */}
      <div className="flex items-center gap-1 flex-wrap bg-gray-900/40 border border-gray-800 rounded-xl p-1">
        {CATEGORY_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = category === tab.key;
          const count = categoryCounts[tab.key] ?? 0;
          // Hide tabs with zero events to reduce noise (except "All").
          if (tab.key !== 'all' && count === 0) return null;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setCategory(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                active
                  ? 'bg-white/10 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${active ? tab.tone : ''}`} />
              {tab.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                active ? 'bg-white/15 text-white' : 'bg-gray-800 text-gray-500'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Top revenue summary strip ────────────────────────────────── */}
      <div className={`grid grid-cols-1 gap-3 ${seesMoney ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
        {seesMoney && (
          <SummaryStat
            label="Booked revenue"
            value={fmtMoney(totals.booked)}
            tone="emerald"
            hint="Across all upcoming festivals"
          />
        )}
        {seesMoney && (
          <SummaryStat
            label="Pending pipeline"
            value={fmtMoney(totals.pending)}
            tone="amber"
            hint="Sponsorships in pending invoice state"
          />
        )}
        <SummaryStat
          label="Slots filled"
          value={`${totals.filledSlots} / ${totals.totalSlots}`}
          tone="cyan"
          hint="Across all upcoming festivals"
        />
        <SummaryStat
          label="Live now"
          value={String(totals.liveCount)}
          tone="purple"
          hint="Festivals running this moment"
        />
      </div>

      {/* ── Festival cards ────────────────────────────────────────────── */}
      {visibleFestivals.length === 0 ? (
        <Card className="bg-gray-900/40 border-gray-800 p-12 text-center">
          <PartyPopper className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400">
            {status === 'inactive'
              ? 'No inactive events.'
              : status === 'all'
                ? 'No events on the calendar.'
                : category === 'all'
                  ? 'No active events.'
                  : `No ${category.replace('_', ' ')} events yet.`}
          </p>
          {status === 'active' && inactiveCount > 0 && (
            <p className="text-amber-300/90 text-xs mt-2">
              {inactiveCount} inactive event{inactiveCount === 1 ? '' : 's'} archived.
              Switch to the Inactive tab above to view or reactivate them.
            </p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Click "+ New event" to add one.
          </p>
          <Button
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-500 text-white mt-4"
          >
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {visibleFestivals.map((f) => (
            <FestivalCard
              key={f.id}
              festival={f}
              onSlotClick={launchWizardForSlot}
              onEdit={() => { setEditing(f); setEditorOpen(true); }}
              onCampaignChanged={fetchFestivals}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      {/* ── Wizard ────────────────────────────────────────────────────── */}
      <CampaignWizard
        mode="festival"
        open={wizardOpen}
        onOpenChange={(o) => {
          setWizardOpen(o);
          if (!o) setWizardPreselect(null);
        }}
        onPublished={() => fetchFestivals()}
      />

      {/* ── Editor dialog ─────────────────────────────────────────────── */}
      <FestivalEditorDialog
        open={editorOpen}
        onOpenChange={(o) => {
          setEditorOpen(o);
          if (!o) setEditing(null);
        }}
        festival={editing}
        onSaved={() => fetchFestivals()}
      />
    </div>
  );
}

// ── SummaryStat ───────────────────────────────────────────────────────────

interface SummaryStatProps {
  label: string;
  value: string;
  tone: 'emerald' | 'amber' | 'cyan' | 'purple';
  hint?: string;
}

function SummaryStat({ label, value, tone, hint }: SummaryStatProps) {
  const tones = {
    emerald: { ring: 'border-emerald-500/30', text: 'text-emerald-300' },
    amber: { ring: 'border-amber-500/30', text: 'text-amber-300' },
    cyan: { ring: 'border-cyan-500/30', text: 'text-cyan-300' },
    purple: { ring: 'border-purple-500/30', text: 'text-purple-300' },
  } as const;
  return (
    <Card className={`bg-gray-900/40 border ${tones[tone].ring} p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${tones[tone].text}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </Card>
  );
}

// ── Festival card ─────────────────────────────────────────────────────────

interface FestivalCardProps {
  festival: FestivalEntry;
  onSlotClick: (
    festival: FestivalEntry,
    tier: FestivalTierKey,
    subEventSlug?: string | null,
  ) => void;
  onEdit: () => void;
  onCampaignChanged: () => void;
  onToggleActive: (festival: FestivalEntry) => void | Promise<void>;
}

function FestivalCard({ festival, onSlotClick, onEdit, onCampaignChanged, onToggleActive }: FestivalCardProps) {
  const { seesMoney } = useAdminAuth();
  const [activeBusy, setActiveBusy] = useState(false);
  const isInactive = festival.is_active === false;

  // Inline visibility verdict — computed client-side from fields the
  // RPC already returns so we don't need a per-card round-trip just to
  // answer "is this on the map RIGHT NOW?". A more authoritative
  // explanation is one click away via the Info button (which calls
  // `admin_festival_visibility`).
  const audience = (festival as any).audience as
    | 'all' | 'passenger' | 'driver' | 'tourist' | undefined;
  const corridorIds = (festival as any).corridor_ids as string[] | undefined;
  const onMapPassenger =
    !isInactive && festival.is_live === true &&
    (audience === undefined || audience === 'all' || audience === 'passenger' || audience === 'tourist');
  const onMapDriver =
    !isInactive && festival.is_live === true &&
    (audience === undefined || audience === 'all' || audience === 'driver');

  // Single-line headline for the "On map" chip. Picks the FIRST
  // disqualifying reason in priority order — same priority used by
  // the visibility RPC. This keeps the chip a glanceable verdict
  // ("Active but not live") and pushes the long-form into the
  // diagnostic dialog reachable via Info.
  let mapHeadline: string;
  let mapTone: 'live' | 'pending' | 'ended' | 'hidden';
  if (isInactive) {
    mapHeadline = 'Inactive, not on any map';
    mapTone = 'hidden';
  } else if (festival.is_live) {
    if (onMapPassenger && onMapDriver) {
      mapHeadline = 'Live on passenger + driver maps';
      mapTone = 'live';
    } else if (onMapPassenger) {
      mapHeadline = 'Live on passenger map only';
      mapTone = 'live';
    } else if (onMapDriver) {
      mapHeadline = 'Live on driver Discover only';
      mapTone = 'live';
    } else {
      mapHeadline = 'Live but no audience matches';
      mapTone = 'hidden';
    }
  } else {
    // Active row whose date window doesn't include NOW. This is the
    // EXACT case operators kept asking about — surface it explicitly.
    const days = festival.days_until_start ?? 0;
    if (days > 0) {
      mapHeadline = `Active, appears on the map in ${days} day${days === 1 ? '' : 's'}`;
      mapTone = 'pending';
    } else if (days < 0) {
      mapHeadline = 'Active, but the date window has ended';
      mapTone = 'ended';
    } else {
      mapHeadline = 'Active, starts today';
      mapTone = 'pending';
    }
  }
  const mapToneClass: Record<typeof mapTone, string> = {
    live:    'bg-emerald-500/15 border-emerald-500/30 text-emerald-200',
    pending: 'bg-amber-500/15 border-amber-500/30 text-amber-200',
    ended:   'bg-gray-800 border-gray-700 text-gray-400',
    hidden:  'bg-rose-500/10 border-rose-500/30 text-rose-200',
  };

  // Authoritative diagnostic dialog state — only fetches when opened
  // so the dashboard isn't issuing N+1 RPC calls on initial load.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState<any>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const openDiagnostic = async () => {
    setDiagOpen(true);
    if (diag) return;
    setDiagLoading(true);
    try {
      const { data, error } = await supabase.rpc(
        'admin_festival_visibility',
        { p_id: festival.id },
      );
      if (error) throw error;
      const env = data as any;
      if (env?.ok === false) throw new Error(env.error || 'failed');
      setDiag(env);
    } catch (err: any) {
      toast.error(err.message || 'Could not load visibility diagnostic');
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const handleToggle = async () => {
    if (activeBusy) return;
    if (
      festival.is_active &&
      !confirm(
        `Deactivate "${festival.name}"?\n\nIt will be hidden from app maps immediately, ` +
          `but stays in the admin (Inactive tab) so you can reactivate it later.`,
      )
    ) {
      return;
    }
    setActiveBusy(true);
    try {
      await onToggleActive(festival);
    } finally {
      setActiveBusy(false);
    }
  };
  const countdown = fmtCountdown(festival.days_until_start ?? 999, !!festival.is_live);
  const filled = festival.revenue?.slot_count_filled ?? 0;
  const total = festival.revenue?.slot_count_total ?? 0;
  const fillPct = total > 0 ? Math.min(100, (filled / total) * 100) : 0;
  const booked = festival.revenue?.booked_xcd ?? 0;
  const pending = festival.revenue?.pending_xcd ?? 0;
  const categoryKey = (festival as any).category as string | undefined;
  const ticketUrl = (festival as any).ticket_url as string | null | undefined;
  const ticketPriceFrom = (festival as any).ticket_price_from_xcd as number | null | undefined;
  const ticketClicks = (festival as any).ticket_clicks as
    | { total: number; last_24h: number; last_7d: number } | undefined;
  const engagement = (festival as any).engagement as
    | {
        total: number;
        last_24h: number;
        last_7d: number;
        opens: number;
        video_plays: number;
        flier_views: number;
        website_clicks: number;
      }
    | undefined;

  // Group sponsorships by tier so the sponsor stack reads
  // tier-by-tier (every Title slot together, every Stage slot, etc.)
  const sponsorshipsByTier = useMemo(() => {
    const map = new Map<FestivalTierKey, FestivalEntry['sponsorships']>();
    festival.sponsorships?.forEach((s) => {
      const list = map.get(s.tier) ?? [];
      list.push(s);
      map.set(s.tier, list);
    });
    return map;
  }, [festival.sponsorships]);

  const tones = {
    live: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
    imminent: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
    upcoming: 'bg-cyan-500/10 border-cyan-500/25 text-cyan-300',
    ended: 'bg-gray-800 border-gray-700 text-gray-400',
  } as const;

  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      className={`overflow-hidden ${
        isInactive
          ? 'bg-gray-900/40 border-amber-500/30'
          : 'bg-gray-900/50 border-gray-800'
      }`}
    >
      {/* Hero strip — slightly desaturated when the event is inactive
          so operators can read "this one is OFF" at a glance even
          before looking at the badges. */}
      <div
        className="h-28 relative overflow-hidden"
        style={{
          background: festival.hero_image_url
            ? `linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.7)), url(${festival.hero_image_url}) center/cover`
            : `linear-gradient(120deg, ${festival.theme_color}, ${festival.theme_color}66)`,
          filter: isInactive ? 'grayscale(0.55) brightness(0.85)' : undefined,
        }}
      >
        <div className="absolute top-3 left-4 flex items-center gap-2">
          <span className="text-3xl">{festival.emoji ?? '🎉'}</span>
          {categoryKey && categoryKey !== 'festival' && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/15 text-white backdrop-blur-sm">
              {CATEGORY_LABEL[categoryKey] || categoryKey}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-4 flex items-center gap-2">
          {isInactive && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-amber-500/15 border-amber-400/40 text-amber-200 inline-flex items-center gap-1 backdrop-blur-sm">
              <EyeOff className="w-3 h-3" />
              Inactive
            </span>
          )}
          {ticketUrl && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-emerald-500/20 border-emerald-400/40 text-emerald-100 inline-flex items-center gap-1 backdrop-blur-sm">
              <Ticket className="w-3 h-3" />
              {ticketPriceFrom != null ? `From EC$${ticketPriceFrom}` : 'Tickets'}
            </span>
          )}
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${tones[countdown.tone]}`}>
            {countdown.label}
          </span>
        </div>
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-white font-bold text-lg leading-tight truncate drop-shadow">
              {festival.name}
            </h2>
            <div className="text-xs text-white/80 mt-0.5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {fmtDateRange(festival.starts_at, festival.ends_at)}
              </span>
              {festival.venue_name && (
                <span className="inline-flex items-center gap-1 truncate max-w-[260px]">
                  <MapPin className="w-3 h-3" />
                  {festival.venue_name}
                </span>
              )}
              {festival.organizer_name && (
                <span className="inline-flex items-center gap-1 truncate max-w-[280px]">
                  <Sparkles className="w-3 h-3" />
                  {festival.organizer_name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Revenue thermometer */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3 text-sm">
              {seesMoney ? (
                <>
                  <span className="text-emerald-300 font-mono font-bold">
                    {fmtMoney(booked)}
                  </span>
                  <span className="text-gray-500">booked</span>
                </>
              ) : (
                <span className="text-gray-400">Sponsor slots</span>
              )}
              {seesMoney && pending > 0 && (
                <>
                  <span className="text-gray-700">·</span>
                  <span className="text-amber-300 font-mono">{fmtMoney(pending)}</span>
                  <span className="text-gray-500">pending</span>
                </>
              )}
            </div>
            <div className="text-xs text-gray-400 font-medium">
              {filled} of {total} slots filled
            </div>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${fillPct}%`,
                background: `linear-gradient(90deg, ${festival.theme_color}, #ffffff44)`,
              }}
            />
          </div>
        </div>

        {/* Ticket-clicks chip — Phase 1 conversion telemetry. Only
            shown when there's something to report (or a ticket URL is
            configured), so we don't add visual noise to events without
            ticketing wired up. */}
        {(ticketUrl || (ticketClicks?.total ?? 0) > 0) && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
              <Ticket className="w-3 h-3" />
              <span className="font-semibold">{ticketClicks?.total ?? 0}</span>
              <span className="text-cyan-300/70">total clicks</span>
            </div>
            {(ticketClicks?.last_24h ?? 0) > 0 && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                <span className="font-semibold">{ticketClicks?.last_24h}</span>
                <span className="text-emerald-300/70">in last 24h</span>
              </div>
            )}
            {!ticketUrl && (
              <span className="text-gray-500 text-[11px]">
                Add a ticket URL via Edit to start tracking conversion.
              </span>
            )}
          </div>
        )}

        {/* Engagement funnel — the top-of-funnel steps that lead to a
            ticket click. Lets operators quote a full story to promoters
            ("620 opened, 300 watched the reel, 140 viewed the flier").
            Only shown once there's engagement to report. */}
        {(engagement?.total ?? 0) > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-gray-500 text-[11px] font-medium mr-0.5">
              Funnel:
            </span>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-300">
              <Eye className="w-3 h-3" />
              <span className="font-semibold">{engagement?.opens ?? 0}</span>
              <span className="text-violet-300/70">opens</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300">
              <Video className="w-3 h-3" />
              <span className="font-semibold">{engagement?.video_plays ?? 0}</span>
              <span className="text-fuchsia-300/70">video</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">
              <ImageIcon className="w-3 h-3" />
              <span className="font-semibold">{engagement?.flier_views ?? 0}</span>
              <span className="text-amber-300/70">flier</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300">
              <Globe className="w-3 h-3" />
              <span className="font-semibold">{engagement?.website_clicks ?? 0}</span>
              <span className="text-sky-300/70">website</span>
            </div>
            {(engagement?.last_24h ?? 0) > 0 && (
              <span className="text-gray-500 text-[11px]">
                {engagement?.last_24h} in last 24h
              </span>
            )}
          </div>
        )}

        {/* On-map verdict — answers the recurring "I marked this active
            but it's not on the map" question without making the
            operator open the SQL. The Info button opens the full
            diagnostic dialog with reason codes, audience, and the
            corridor scope. */}
        <div
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-[11px] font-semibold ${mapToneClass[mapTone]}`}
        >
          {mapTone === 'live' ? (
            <Eye className="w-3.5 h-3.5" />
          ) : mapTone === 'pending' ? (
            <CalendarDays className="w-3.5 h-3.5" />
          ) : (
            <EyeOff className="w-3.5 h-3.5" />
          )}
          <span className="flex-1 truncate">{mapHeadline}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={openDiagnostic}
                className="opacity-70 hover:opacity-100"
                aria-label="Why is this event on / off the map?"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
              Inspect visibility
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="border-gray-700 hover:border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 text-xs"
            onClick={() => setExpanded(!expanded)}
          >
            <ChevronRight
              className={`w-3.5 h-3.5 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
            {expanded ? 'Hide sponsor stack' : 'View sponsor stack'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-700 hover:border-amber-500/50 hover:bg-amber-500/10 text-amber-200 text-xs"
            onClick={onEdit}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
          {/* One-click activate / deactivate. The whole reason for the
              Inactive tab existing — without this, an operator who paused
              an event would still need to open the full editor dialog to
              flip the boolean back. */}
          <Button
            variant="outline"
            size="sm"
            disabled={activeBusy}
            onClick={handleToggle}
            className={
              isInactive
                ? 'border-emerald-500/40 hover:border-emerald-500/60 hover:bg-emerald-500/10 text-emerald-300 text-xs'
                : 'border-gray-700 hover:border-amber-500/50 hover:bg-amber-500/10 text-amber-200 text-xs'
            }
          >
            {activeBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : isInactive ? (
              <Power className="w-3.5 h-3.5 mr-1" />
            ) : (
              <PowerOff className="w-3.5 h-3.5 mr-1" />
            )}
            {isInactive ? 'Activate' : 'Deactivate'}
          </Button>
          <Link href={`/admin/festivals/${festival.slug}/pitch`}>
            <Button
              variant="outline"
              size="sm"
              className="border-gray-700 hover:border-purple-500/50 hover:bg-purple-500/10 text-purple-300 text-xs"
            >
              <Printer className="w-3.5 h-3.5 mr-1" />
              Pitch deck
            </Button>
          </Link>
          {ticketUrl && (
            <a href={ticketUrl} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                size="sm"
                className="border-gray-700 hover:border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 text-xs"
              >
                <Ticket className="w-3.5 h-3.5 mr-1" />
                Open tickets
              </Button>
            </a>
          )}
          {festival.external_url && (
            <a href={festival.external_url} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                size="sm"
                className="border-gray-700 hover:border-cyan-500/50 hover:bg-cyan-500/10 text-cyan-300 text-xs"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Event website
              </Button>
            </a>
          )}
        </div>

        {/* Sponsor stack — collapsible */}
        {expanded && (
          <div className="bg-black/30 rounded-lg border border-gray-800 p-3 space-y-3">
            {TIER_ORDER.map((tier) => {
              const cfg = festival.sponsorship_tiers.find((t) => t.tier === tier);
              if (!cfg) return null;
              const visual = TIER_VISUAL[tier];
              const filledList = sponsorshipsByTier.get(tier) ?? [];
              const openCount = Math.max(0, cfg.slot_count - filledList.length);
              const Icon = visual.icon;
              return (
                <div key={tier}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-3.5 h-3.5" style={{ color: visual.accent }} />
                    <span className="text-xs font-semibold text-white">{visual.label}</span>
                    <span
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                      style={{
                        background: `${visual.accent}22`,
                        color: visual.accent,
                      }}
                    >
                      EC${cfg.rate_xcd_min.toLocaleString()}–{cfg.rate_xcd_max.toLocaleString()}
                    </span>
                    <span className="text-[10px] text-gray-500 ml-auto">
                      {filledList.length} of {cfg.slot_count} slots
                    </span>
                  </div>
                  <div className="space-y-1">
                    {filledList.map((s) => (
                      <SponsorRow
                        key={s.id}
                        sponsorship={s}
                        accent={visual.accent}
                        onChanged={onCampaignChanged}
                      />
                    ))}
                    {Array.from({ length: openCount }).map((_, i) => (
                      <button
                        key={`open-${tier}-${i}`}
                        type="button"
                        onClick={() => onSlotClick(festival, tier)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-dashed border-gray-700 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all group"
                      >
                        <div className="flex items-center gap-2 text-xs text-gray-500 group-hover:text-emerald-300">
                          <Plus className="w-3.5 h-3.5" />
                          Open slot. Sell it to a sponsor
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-600 group-hover:text-emerald-300" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Visibility diagnostic dialog ─────────────────────────────
          Opens when the operator clicks the Info button on the on-map
          chip. Renders the structured response from
          `admin_festival_visibility` as a plain-English checklist so
          there's no ambiguity about WHY an event is or isn't on the
          map. */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-4 h-4 text-emerald-400" />
              Map visibility: {festival.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-400">
              The in-app map shows an event only when ALL conditions
              below are true. Operator-visible status:
            </DialogDescription>
          </DialogHeader>

          {diagLoading || !diag ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading diagnostic…
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              {/* On-map verdict row */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded border ${diag.on_map_passenger ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-gray-800 border-gray-700'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Smartphone className="w-3.5 h-3.5" />
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">Passenger</span>
                  </div>
                  <div className={`text-xs font-bold ${diag.on_map_passenger ? 'text-emerald-300' : 'text-gray-500'}`}>
                    {diag.on_map_passenger ? 'On map' : 'Hidden'}
                  </div>
                </div>
                <div className={`p-2 rounded border ${diag.on_map_driver ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-gray-800 border-gray-700'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Car className="w-3.5 h-3.5" />
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">Driver Discover</span>
                  </div>
                  <div className={`text-xs font-bold ${diag.on_map_driver ? 'text-emerald-300' : 'text-gray-500'}`}>
                    {diag.on_map_driver ? 'On map' : 'Hidden'}
                  </div>
                </div>
              </div>

              {/* Condition checklist — green check = passing, red dot = blocking */}
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-start gap-2">
                  <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${diag.is_active ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                  <span>
                    <strong className="text-gray-100">Active flag:</strong>{' '}
                    {diag.is_active ? 'enabled' : 'disabled. Use Activate to publish'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${diag.window?.in_window ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                  <span>
                    <strong className="text-gray-100">Date window:</strong>{' '}
                    {diag.window?.in_window
                      ? 'currently inside the start–end window'
                      : (diag.window?.days_until_start ?? 0) > 0
                        ? `starts in ${diag.window.days_until_start} day${diag.window.days_until_start === 1 ? '' : 's'}, so it won't appear on the map until ${new Date(diag.window.starts_at).toLocaleString()}`
                        : `ended ${diag.window?.days_after_end ?? 0} day${(diag.window?.days_after_end ?? 0) === 1 ? '' : 's'} ago on ${new Date(diag.window.ends_at).toLocaleString()}`}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span>
                    <strong className="text-gray-100">Audience:</strong>{' '}
                    {diag.audience === 'all'
                      ? 'everyone (passengers + drivers + tourists)'
                      : diag.audience === 'passenger'
                        ? 'passengers only'
                        : diag.audience === 'driver'
                          ? 'drivers only (Discover tab)'
                          : diag.audience === 'tourist'
                            ? 'tourist mode only'
                            : diag.audience}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span>
                    <strong className="text-gray-100">Corridor scope:</strong>{' '}
                    {Array.isArray(diag.corridor_ids) && diag.corridor_ids.length > 0
                      ? `scoped to ${diag.corridor_ids.length} corridor${diag.corridor_ids.length === 1 ? '' : 's'}, so passengers off those corridors won't see it`
                      : 'island-wide (no corridor restriction)'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${diag.has_venue ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span>
                    <strong className="text-gray-100">Venue coordinates:</strong>{' '}
                    {diag.has_venue
                      ? 'set, so the glow pin shows on the map'
                      : 'missing. Set the venue lat/lng in the editor so users can navigate to the event'}
                  </span>
                </li>
              </ul>

              {/* Headline reason — surfaced as a friendly callout when
                  the event is currently NOT visible on either map. */}
              {!diag.on_map_passenger && !diag.on_map_driver && (
                <div className="mt-2 p-2 rounded border border-amber-500/30 bg-amber-500/10 text-amber-100 text-xs">
                  <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Not currently on any map.</strong>{' '}
                      {Array.isArray(diag.reasons) && diag.reasons.length > 0
                        ? `Blocked by: ${diag.reasons.join(', ').replace(/_/g, ' ')}.`
                        : 'Adjust the conditions above and refresh.'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="border-gray-700 hover:bg-gray-800 text-gray-300 text-xs"
              onClick={() => setDiagOpen(false)}
            >
              Close
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
              onClick={() => { setDiagOpen(false); onEdit(); }}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              Edit event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── SponsorRow ────────────────────────────────────────────────────────────
//
// One row per filled sponsorship slot. This is the operator's main
// surface for managing campaigns linked to a festival — without this,
// the only way to pause / delete / preview was to context-switch to the
// /admin/sponsors table and search for the brand. The row carries:
//
//   • Brand identity (logo, name, sub-event)
//   • Schedule chip — Live / Scheduled in N days / Ended / Previewing /
//     Paused / Inactive — derived from the underlying ad_campaigns row
//   • Invoice chip (existing)
//   • Action buttons:
//       Eye   — admin_preview_campaign_now (15min QA window)
//       RotateCcw — admin_revert_campaign_schedule (only when in preview)
//       Pause/Play — toggle is_paused on ad_campaigns
//       Settings — link to /admin/sponsors for full edit
//       Trash2 — delete the campaign (cascades the festival_sponsorships row)
//
// All actions invoke `onChanged` so the parent re-fetches the festival
// list; we do NOT optimistically mutate state because the schedule
// chip depends on derived RPC fields that would drift if we tried to
// update locally.
// ─────────────────────────────────────────────────────────────────────────

interface SponsorRowProps {
  sponsorship: NonNullable<FestivalEntry['sponsorships']>[number];
  accent: string;
  onChanged: () => void;
}

type ScheduleChip =
  | { tone: 'live'; label: string }
  | { tone: 'scheduled'; label: string }
  | { tone: 'ended'; label: string }
  | { tone: 'preview'; label: string }
  | { tone: 'paused'; label: string }
  | { tone: 'inactive'; label: string }
  | { tone: 'unknown'; label: string };

function deriveScheduleChip(s: SponsorRowProps['sponsorship']): ScheduleChip {
  if (s.is_paused) return { tone: 'paused', label: 'Paused' };
  if (s.campaign_active === false) return { tone: 'inactive', label: 'Inactive' };
  if (s.is_preview_active) return { tone: 'preview', label: 'Previewing live' };

  const start = s.campaign_starts_at ? new Date(s.campaign_starts_at).getTime() : null;
  const end   = s.campaign_ends_at   ? new Date(s.campaign_ends_at).getTime()   : null;
  const now = Date.now();

  if (start === null || end === null) return { tone: 'unknown', label: 'No schedule' };
  if (now < start) {
    const days = Math.max(0, Math.ceil((start - now) / 86400000));
    if (days <= 0) return { tone: 'scheduled', label: 'Starts today' };
    return { tone: 'scheduled', label: `Live in ${days}d` };
  }
  if (now > end) return { tone: 'ended', label: 'Ended' };
  return { tone: 'live', label: 'Live now' };
}

const SCHEDULE_CHIP_STYLES: Record<ScheduleChip['tone'], string> = {
  live:      'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
  scheduled: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300',
  ended:     'bg-gray-800 border-gray-700 text-gray-400',
  preview:   'bg-amber-500/15 border-amber-500/40 text-amber-200',
  paused:    'bg-amber-500/10 border-amber-500/30 text-amber-300',
  inactive:  'bg-gray-800 border-gray-700 text-gray-500',
  unknown:   'bg-gray-800 border-gray-700 text-gray-500',
};

function SponsorRow({ sponsorship, accent, onChanged }: SponsorRowProps) {
  const { isManager } = useAdminAuth();
  const statusColor = {
    pending: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    invoiced: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
    paid: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
    void: 'bg-gray-800 text-gray-500 border-gray-700',
  }[sponsorship.invoice_status] || 'bg-gray-800 text-gray-500 border-gray-700';

  const schedule = deriveScheduleChip(sponsorship);
  const [busy, setBusy] = useState<null | 'preview' | 'revert' | 'pause' | 'delete'>(null);

  const refresh = () => onChanged();

  const handlePreviewNow = async () => {
    setBusy('preview');
    try {
      const { data, error } = await supabase.rpc('admin_preview_campaign_now', {
        p_campaign_id: sponsorship.campaign_id,
        p_minutes_window: 15,
      });
      if (error) throw error;
      const env = data as { ok?: boolean; error?: string; preview_minutes?: number };
      if (env?.ok === false) {
        if (env.error === 'campaign_paused') {
          toast.error('Resume the campaign before previewing.');
        } else if (env.error === 'campaign_inactive') {
          toast.error('Campaign is inactive. Activate it first.');
        } else {
          toast.error(env.error || 'Preview failed');
        }
        return;
      }
      toast.success(
        `${sponsorship.brand_name} is live for the next ${env?.preview_minutes ?? 15} minutes. Open the H-Plate app on your device.`,
        { duration: 8000 },
      );
      refresh();
    } catch (err: any) {
      toast.error(err.message || 'Preview failed');
    } finally {
      setBusy(null);
    }
  };

  const handleRevert = async () => {
    setBusy('revert');
    try {
      const { data, error } = await supabase.rpc('admin_revert_campaign_schedule', {
        p_campaign_id: sponsorship.campaign_id,
      });
      if (error) throw error;
      const env = data as { ok?: boolean; reverted?: boolean; error?: string };
      if (env?.ok === false) throw new Error(env.error || 'Revert failed');
      if (env?.reverted) {
        toast.success(`Restored ${sponsorship.brand_name}'s original schedule.`);
      } else {
        toast.info('Already on its real schedule. Nothing to revert.');
      }
      refresh();
    } catch (err: any) {
      toast.error(err.message || 'Revert failed');
    } finally {
      setBusy(null);
    }
  };

  const handlePauseToggle = async () => {
    setBusy('pause');
    const newPaused = !sponsorship.is_paused;
    try {
      const { error } = await supabase
        .from('ad_campaigns')
        .update({ is_paused: newPaused })
        .eq('id', sponsorship.campaign_id);
      if (error) throw error;
      toast.success(
        newPaused
          ? `${sponsorship.brand_name} paused`
          : `${sponsorship.brand_name} resumed`,
      );
      refresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle pause');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!confirm(
      `Delete ${sponsorship.brand_name}'s ${sponsorship.tier} sponsorship?\n\n` +
      `This removes the campaign entirely, along with its link to this festival. ` +
      `If you just want to take it off the air, use Pause instead.`,
    )) return;
    setBusy('delete');
    try {
      const { error } = await supabase
        .from('ad_campaigns')
        .delete()
        .eq('id', sponsorship.campaign_id);
      if (error) throw error;
      toast.success(`${sponsorship.brand_name} deleted`);
      refresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete campaign');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-md border bg-gray-900/40"
      style={{ borderColor: `${accent}33` }}
    >
      {sponsorship.logo_url ? (
        <img
          src={sponsorship.logo_url}
          alt=""
          className="w-7 h-7 rounded-full object-cover border border-gray-700 shrink-0"
        />
      ) : (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
          style={{ background: `${accent}33`, color: accent }}
        >
          {sponsorship.brand_name.charAt(0).toUpperCase()}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-xs text-white font-medium truncate">
          {sponsorship.brand_name}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${SCHEDULE_CHIP_STYLES[schedule.tone]}`}>
            {schedule.label}
          </span>
          {sponsorship.sub_event_slug && (
            <span className="text-[10px] text-gray-500 truncate">
              sub-event: {sponsorship.sub_event_slug}
            </span>
          )}
        </div>
      </div>

      {sponsorship.contracted_amount_xcd != null && (
        <div className="text-xs font-mono text-emerald-300 shrink-0">
          {fmtMoney(sponsorship.contracted_amount_xcd)}
        </div>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`text-[10px] capitalize ${statusColor}`}>
            {sponsorship.invoice_status}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {sponsorship.invoice_number ? `Invoice #${sponsorship.invoice_number}` : 'Invoice status'}
        </TooltipContent>
      </Tooltip>

      {/* ── Action buttons ───────────────────────────────────────────── */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Preview — pulls a future-dated campaign forward for 15min QA */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-amber-300 w-7 h-7"
              onClick={handlePreviewNow}
              disabled={busy !== null || sponsorship.is_paused || sponsorship.campaign_active === false}
            >
              {busy === 'preview' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
            Preview live for 15 minutes on your device
          </TooltipContent>
        </Tooltip>

        {sponsorship.is_preview_active && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-amber-300 hover:text-amber-200 w-7 h-7"
                onClick={handleRevert}
                disabled={busy !== null}
              >
                {busy === 'revert' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
              Restore original schedule
            </TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-amber-300 w-7 h-7"
              onClick={handlePauseToggle}
              disabled={busy !== null}
            >
              {busy === 'pause' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : sponsorship.is_paused ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
            {sponsorship.is_paused ? 'Resume' : 'Pause'}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Link href={`/admin/sponsors?id=${sponsorship.campaign_id}`}>
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white w-7 h-7">
                <Settings className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
            Open in Sponsors for full edit
          </TooltipContent>
        </Tooltip>

        {isManager && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-rose-400 w-7 h-7"
                onClick={handleDelete}
                disabled={busy !== null}
                aria-label={`Delete ${sponsorship.brand_name}'s sponsorship`}
              >
                {busy === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
              Delete campaign
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
